import React, { useState, useMemo, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { usePrinters } from '../../hooks/usePrinters'
import { useFilamentSpools } from '../../hooks/useFilamentSpools'
import { useSettings } from '../../hooks/useSettings'
import {
  usePrints,
  usePrintBuilds,
  useCreatePrint,
  useUpdatePrint,
  PRINT_PURPOSES,
  PRINT_OUTCOMES,
} from '../../hooks/usePrints'
import { useProducts, useCreateProduct } from '../../hooks/useProducts'
import { useBuilds, useCreateBuild, useUpdateBuild } from '../../hooks/useBuilds'
import { computePrintCosts, filamentCostForUsage } from '../../lib/pricing'
import { formatMoney } from '../../lib/format'
import { supabase } from '../../lib/supabase'
import type { Build } from '../../lib/database.types'
import { Field } from '../../components/ui/Field'
import { BackLink } from '../../components/BackLink'
import { useToast } from '../../components/ToastContext'

// Compute the next sequence number for a product's builds (e.g., "Build #3")
function nextBuildNameForProduct(
  productId: string,
  allBuilds: Build[],
): string {
  const existing = allBuilds.filter((b) => b.product_id === productId).length
  return `Build #${existing + 1}`
}

interface UsageDraft {
  spoolId: string
  grams: string
}

// One additional product produced by the same plate. The primary product/build
// is tracked in the top-level form state (productId, buildSelection, etc.).
// Secondaries live in this array and are rare — used when a plate happens to
// have multiple different items on it.
interface SecondaryOutputDraft {
  productId: string // existing id, '' (no product), or '__new__' (inline create)
  newProductName: string
  buildSelection: string // '__new_single__' | '__new_multi__' | existing build id
  units: string
}

export default function PrintForm() {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const isEdit = Boolean(id)
  const { toast } = useToast()

  const { data: printers } = usePrinters()
  const { data: spools } = useFilamentSpools()
  const { data: settings } = useSettings()
  const { data: existingPrints } = usePrints()
  const createPrint = useCreatePrint()
  const updatePrint = useUpdatePrint()
  const createProduct = useCreateProduct()
  const { data: builds } = useBuilds()
  const createBuild = useCreateBuild()
  const updateBuild = useUpdateBuild()
  const { data: printBuilds } = usePrintBuilds()

  const existing = existingPrints?.find((p) => p.id === id)

  const today = new Date().toISOString().slice(0, 10)

  const initialPrinterId = existing?.printer_id ?? printers?.[0]?.id ?? ''
  const initialPrintDate = existing?.print_date ?? today
  const initialPurpose = (existing?.purpose ?? 'business-sale') as typeof PRINT_PURPOSES[number]
  const initialUnits = existing ? String(existing.units_produced) : '1'
  const initialProductId = existing?.product_id ?? ''
  const initialPrintTime = existing ? String(existing.print_time_hours) : ''
  const initialLabor = existing ? String(existing.labor_minutes) : '0'
  const initialOutcome = (existing?.outcome ?? 'success') as typeof PRINT_OUTCOMES[number]
  const initialDescription = existing?.description ?? ''
  const initialModelSource = existing?.model_source ?? ''
  const initialSlicerName = existing?.slicer_name ?? ''
  const initialSlicerProfile = existing?.slicer_profile ?? ''
  const initialLayerHeight = existing?.layer_height_mm != null ? String(existing.layer_height_mm) : ''
  const initialInfillPct = existing?.infill_pct != null ? String(existing.infill_pct) : ''
  const initialInfillPattern = existing?.infill_pattern ?? ''
  const initialPrintSpeed = existing?.print_speed_mms != null ? String(existing.print_speed_mms) : ''
  const initialNozzleTemp = existing?.nozzle_temp_c != null ? String(existing.nozzle_temp_c) : ''
  const initialBedTemp = existing?.bed_temp_c != null ? String(existing.bed_temp_c) : ''
  const initialSupports = existing?.supports ?? ''
  const initialSurfaceQuality = existing?.surface_quality != null ? String(existing.surface_quality) : ''
  const initialDimensionalAccuracy = existing?.dimensional_accuracy != null ? String(existing.dimensional_accuracy) : ''
  const initialFailureMode = existing?.failure_mode ?? ''
  const initialObservations = existing?.observations ?? ''
  const initialLessonsLearned = existing?.lessons_learned ?? ''

  const [printerId, setPrinterId] = useState(initialPrinterId)
  const [printDate, setPrintDate] = useState(initialPrintDate)
  const [purpose, setPurpose] = useState<typeof PRINT_PURPOSES[number]>(initialPurpose)
  const [unitsProduced, setUnitsProduced] = useState(initialUnits)
  const [productId, setProductId] = useState<string>(initialProductId)
  const { data: products } = useProducts()
  const [usages, setUsages] = useState<UsageDraft[]>([])

  // On mount in edit mode, fetch existing filament usages to pre-fill the rows.
  // Intentionally depends only on existing.id — we only want to load once when
  // the existing print record first becomes available, not on every render.
  const existingId = existing?.id
  useEffect(() => {
    if (!existingId) return
    let cancelled = false
    supabase
      .from('print_filament_usage')
      .select('spool_id, grams_used')
      .eq('print_id', existingId)
      .then(({ data }) => {
        if (cancelled || !data) return
        setUsages(data.map((u) => ({ spoolId: u.spool_id, grams: String(u.grams_used) })))
      })
    return () => {
      cancelled = true
    }
  }, [existingId])

  const [printTimeHours, setPrintTimeHours] = useState(initialPrintTime)
  const [laborMinutes, setLaborMinutes] = useState(initialLabor)
  const [outcome, setOutcome] = useState<typeof PRINT_OUTCOMES[number]>(initialOutcome)
  const [description, setDescription] = useState(initialDescription)
  const [modelSource, setModelSource] = useState(initialModelSource)
  const [showDetails, setShowDetails] = useState(isEdit)
  const [errorMessage, setErrorMessage] = useState('')

  // "+ Create new product" UI state
  const [newProductName, setNewProductName] = useState('')
  const isCreatingNewProduct = productId === '__new__'

  // Build picker state. Options: '__new_single__', '__new_multi__', existing build id, '__detached__'
  const [buildSelection, setBuildSelection] = useState<string>('__new_single__')

  // Secondary products produced by the same plate (rare — mixed plates).
  const [secondaryOutputs, setSecondaryOutputs] = useState<SecondaryOutputDraft[]>([])

  // Edit mode: preload primary build selection + secondaries from the junction.
  // The primary build is whatever existing.build_id points to; any OTHER builds
  // attached via the junction become secondary outputs.
  useEffect(() => {
    if (!existing || !printBuilds || !builds) return
    // Defer state updates via microtask so they happen outside the effect body
    // (avoids react-hooks/set-state-in-effect lint warning).
    Promise.resolve().then(() => {
      const attachedBuildIds = printBuilds
        .filter((pb) => pb.print_id === existing.id)
        .map((pb) => pb.build_id)

      if (existing.build_id) {
        setBuildSelection(existing.build_id)
        // The build's units field is the source of truth for inventory.
        // Prefer it over prints.units_produced (which can drift).
        const primaryBuild = builds.find((b) => b.id === existing.build_id)
        if (primaryBuild) setUnitsProduced(String(primaryBuild.units))
      } else if (attachedBuildIds.length === 0) {
        setBuildSelection('__detached__')
      }

      const secondaryBuildIds = attachedBuildIds.filter((b) => b !== existing.build_id)
      const secondaries: SecondaryOutputDraft[] = secondaryBuildIds
        .map((bid) => builds.find((b) => b.id === bid))
        .filter((b): b is Build => b != null)
        .map((b) => ({
          productId: b.product_id,
          newProductName: '',
          buildSelection: b.id,
          units: String(b.units),
        }))
      setSecondaryOutputs(secondaries)
    })
    // We only want to run this once when existing first becomes available.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.id, printBuilds?.length, builds?.length])

  const [slicerName, setSlicerName] = useState(initialSlicerName)
  const [slicerProfile, setSlicerProfile] = useState(initialSlicerProfile)
  const [layerHeight, setLayerHeight] = useState(initialLayerHeight)
  const [infillPct, setInfillPct] = useState(initialInfillPct)
  const [infillPattern, setInfillPattern] = useState(initialInfillPattern)
  const [printSpeed, setPrintSpeed] = useState(initialPrintSpeed)
  const [nozzleTemp, setNozzleTemp] = useState(initialNozzleTemp)
  const [bedTemp, setBedTemp] = useState(initialBedTemp)
  const [supports, setSupports] = useState(initialSupports)
  const [surfaceQuality, setSurfaceQuality] = useState(initialSurfaceQuality)
  const [dimensionalAccuracy, setDimensionalAccuracy] = useState(initialDimensionalAccuracy)
  const [failureMode, setFailureMode] = useState(initialFailureMode)
  const [observations, setObservations] = useState(initialObservations)
  const [lessonsLearned, setLessonsLearned] = useState(initialLessonsLearned)

  // Sync printer default once it's available (no useEffect — derived)
  const effectivePrinterId = printerId || (printers?.[0]?.id ?? '')

  const activePrinter = printers?.find((p) => p.id === effectivePrinterId)
  const spoolById = useMemo(
    () => new Map((spools ?? []).map((s) => [s.id, s])),
    [spools],
  )

  const costBreakdown = useMemo(() => {
    if (!activePrinter || !settings || !printTimeHours) return null
    const usagesForCalc = usages
      .filter((u) => u.spoolId && Number(u.grams) > 0)
      .map((u) => {
        const spool = spoolById.get(u.spoolId)
        if (!spool) return null
        return {
          grams_used: Number(u.grams),
          spool: {
            purchase_price: spool.purchase_price,
            original_weight_grams: spool.original_weight_grams,
          },
        }
      })
      .filter((x): x is NonNullable<typeof x> => x != null)

    return computePrintCosts({
      printer: activePrinter,
      settings,
      printTimeHours: Number(printTimeHours),
      laborMinutes: Number(laborMinutes),
      usages: usagesForCalc,
    })
  }, [activePrinter, settings, printTimeHours, laborMinutes, usages, spoolById])

  // Resolve a single output (product + build) into concrete IDs, creating new
  // rows as needed. Returns null buildId for detached.
  const resolveOutput = async (
    output: {
      productSelection: string // existing id, '__new__', or ''
      newProductName: string
      buildSelection: string
      units: number
    },
    totalFilamentGrams: number,
  ): Promise<{ productId: string | null; buildId: string | null }> => {
    let prodId: string | null = null
    if (output.productSelection === '__new__') {
      if (!output.newProductName.trim()) {
        throw new Error('Enter a name for the new product')
      }
      const newProduct = await createProduct.mutateAsync({
        name: output.newProductName.trim(),
        typical_print_time_hours: Number(printTimeHours),
        typical_filament_grams: totalFilamentGrams,
        typical_labor_minutes: Number(laborMinutes),
      })
      prodId = newProduct.id
    } else if (output.productSelection) {
      prodId = output.productSelection
    }

    let bid: string | null = null
    if (prodId && output.buildSelection && output.buildSelection !== '__detached__') {
      if (output.buildSelection === '__new_single__') {
        const newBuild = await createBuild.mutateAsync({
          product_id: prodId,
          name: nextBuildNameForProduct(prodId, builds ?? []),
          status: 'completed',
          units: output.units || 1,
          completed_at: new Date().toISOString(),
        })
        bid = newBuild.id
      } else if (output.buildSelection === '__new_multi__') {
        const newBuild = await createBuild.mutateAsync({
          product_id: prodId,
          name: nextBuildNameForProduct(prodId, builds ?? []),
          status: 'in_progress',
          units: output.units || 1,
        })
        bid = newBuild.id
      } else {
        // Existing build id
        bid = output.buildSelection
      }
    }
    return { productId: prodId, buildId: bid }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage('')

    if (purpose === 'business-sale' && !description.trim()) {
      setErrorMessage('Name is required for business-sale prints')
      return
    }

    if (!activePrinter || !settings) {
      setErrorMessage('Missing printer or settings')
      return
    }

    const validUsages = usages.filter((u) => u.spoolId && Number(u.grams) > 0)
    if (!isEdit && validUsages.length === 0) {
      setErrorMessage('Add at least one filament spool with grams used')
      return
    }
    const totalFilamentGrams = validUsages.reduce((sum, u) => sum + Number(u.grams), 0)

    // Resolve PRIMARY output
    let primaryResolved: { productId: string | null; buildId: string | null }
    try {
      primaryResolved = await resolveOutput(
        {
          productSelection: isCreatingNewProduct ? '__new__' : productId,
          newProductName,
          buildSelection: purpose === 'business-sale' ? buildSelection : '__detached__',
          units: Number(unitsProduced) || 1,
        },
        totalFilamentGrams,
      )
    } catch (err) {
      setErrorMessage((err as Error).message)
      return
    }
    const primaryProductId = primaryResolved.productId
    const primaryBuildId = primaryResolved.buildId

    // Resolve SECONDARY outputs (additional products on the same plate)
    const secondaryBuildIds: string[] = []
    // Track (existing build id, target units) pairs so we can sync units after save.
    // resolveOutput sets units only when CREATING a new build; for existing-build
    // attachments we have to update them ourselves.
    const secondaryUnitsSyncs: Array<{ buildId: string; units: number }> = []
    if (purpose === 'business-sale') {
      for (const s of secondaryOutputs) {
        // Skip empty rows (no product selected)
        if (!s.productId) continue
        try {
          const resolved = await resolveOutput(
            {
              productSelection: s.productId,
              newProductName: s.newProductName,
              buildSelection: s.buildSelection,
              units: Number(s.units) || 1,
            },
            totalFilamentGrams,
          )
          if (resolved.buildId) {
            secondaryBuildIds.push(resolved.buildId)
            const isExistingBuild =
              s.buildSelection !== '__new_single__' &&
              s.buildSelection !== '__new_multi__' &&
              s.buildSelection !== '__detached__' &&
              s.buildSelection !== ''
            if (isExistingBuild) {
              secondaryUnitsSyncs.push({ buildId: resolved.buildId, units: Number(s.units) || 1 })
            }
          }
        } catch (err) {
          setErrorMessage('Failed to resolve additional product: ' + (err as Error).message)
          return
        }
      }
    }

    const allBuildIds = [primaryBuildId, ...secondaryBuildIds].filter(
      (b): b is string => Boolean(b),
    )

    const mappedUsages = validUsages.map((u) => {
      const spool = spoolById.get(u.spoolId)!
      const cost = filamentCostForUsage(
        Number(u.grams),
        spool,
        Number(settings.material_efficiency_factor),
      )
      return {
        spool_id: u.spoolId,
        grams_used: Number(u.grams),
        cost_recognized: cost,
      }
    })

    // Compute the cost breakdown for BOTH create and update paths. The cached
    // cost fields on the prints row (filament_cost_total, machine_cost, …)
    // must always reflect the current usages + hours + labor; otherwise a
    // print that gets edited after the fact carries stale numbers into every
    // dashboard. (This used to be computed only on create — that bug left
    // prints with $0 filament cost even when usage rows had the right
    // cost_recognized.)
    const computed =
      costBreakdown ?? {
        filamentCost: 0,
        electricityCost: 0,
        machineCost: 0,
        laborCost: 0,
        totalCost: 0,
      }

    if (isEdit && id) {
      try {
        await updatePrint.mutateAsync({
          id,
          updates: {
            print_date: printDate,
            purpose,
            product_id: primaryProductId,
            units_produced: Number(unitsProduced),
            print_time_hours: Number(printTimeHours),
            labor_minutes: Number(laborMinutes),
            outcome,
            description: description || null,
            model_source: modelSource || null,
            surface_quality: surfaceQuality ? Number(surfaceQuality) : null,
            dimensional_accuracy: dimensionalAccuracy ? Number(dimensionalAccuracy) : null,
            failure_mode: failureMode || null,
            slicer_name: slicerName || null,
            slicer_profile: slicerProfile || null,
            layer_height_mm: layerHeight ? Number(layerHeight) : null,
            infill_pct: infillPct ? Number(infillPct) : null,
            infill_pattern: infillPattern || null,
            print_speed_mms: printSpeed ? Number(printSpeed) : null,
            nozzle_temp_c: nozzleTemp ? Number(nozzleTemp) : null,
            bed_temp_c: bedTemp ? Number(bedTemp) : null,
            supports: supports || null,
            observations: observations || null,
            lessons_learned: lessonsLearned || null,
            // ── Cost fields recomputed from current usages + settings ──
            filament_cost_total: computed.filamentCost,
            electricity_cost: computed.electricityCost,
            machine_cost: computed.machineCost,
            labor_cost: computed.laborCost,
            total_cost: computed.totalCost,
          },
          usages: mappedUsages,
          buildIds: allBuildIds,
        })
        // Sync the primary build to match what the user entered. The build
        // owns the inventory truth via builds.product_id and builds.units —
        // changing the print's product/units doesn't move inventory unless
        // we propagate to the build itself.
        if (primaryBuildId) {
          const primaryBuild = builds?.find((b) => b.id === primaryBuildId)
          if (primaryBuild) {
            const newUnits = Number(unitsProduced) || 1
            const patch: { units?: number; product_id?: string } = {}
            if (primaryBuild.units !== newUnits) patch.units = newUnits
            if (primaryProductId && primaryBuild.product_id !== primaryProductId) {
              patch.product_id = primaryProductId
            }
            if (Object.keys(patch).length > 0) {
              await updateBuild.mutateAsync({ id: primaryBuildId, ...patch })
            }
          }
        }
        // Same for any secondary outputs attached to existing builds.
        // For secondaries, the product_id is already enforced via resolveOutput
        // (existing-build selection presupposes the matching product). Only
        // units need to sync here.
        for (const sync of secondaryUnitsSyncs) {
          const b = builds?.find((bb) => bb.id === sync.buildId)
          if (b && b.units !== sync.units) {
            await updateBuild.mutateAsync({ id: sync.buildId, units: sync.units })
          }
        }
        toast({ tone: 'success', text: 'Print updated' })
        navigate('/prints')
      } catch (err) {
        setErrorMessage((err as Error).message)
        toast({ tone: 'error', text: 'Failed to save: ' + (err as Error).message })
      }
      return
    }

    try {
      await createPrint.mutateAsync({
        print: {
          printer_id: effectivePrinterId,
          print_date: printDate,
          purpose,
          product_id: primaryProductId,
          build_id: primaryBuildId,
          units_produced: Number(unitsProduced),
          print_time_hours: Number(printTimeHours),
          labor_minutes: Number(laborMinutes),
          outcome,
          surface_quality: surfaceQuality ? Number(surfaceQuality) : null,
          dimensional_accuracy: dimensionalAccuracy ? Number(dimensionalAccuracy) : null,
          failure_mode: failureMode || null,
          slicer_name: slicerName || null,
          slicer_profile: slicerProfile || null,
          layer_height_mm: layerHeight ? Number(layerHeight) : null,
          infill_pct: infillPct ? Number(infillPct) : null,
          infill_pattern: infillPattern || null,
          print_speed_mms: printSpeed ? Number(printSpeed) : null,
          nozzle_temp_c: nozzleTemp ? Number(nozzleTemp) : null,
          bed_temp_c: bedTemp ? Number(bedTemp) : null,
          supports: supports || null,
          description: description || null,
          model_source: modelSource || null,
          observations: observations || null,
          lessons_learned: lessonsLearned || null,
          filament_cost_total: computed.filamentCost,
          electricity_cost: computed.electricityCost,
          machine_cost: computed.machineCost,
          labor_cost: computed.laborCost,
          total_cost: computed.totalCost,
        },
        usages: mappedUsages,
        buildIds: allBuildIds,
      })
      toast({ tone: 'success', text: 'Print logged' })
      navigate('/prints')
    } catch (err) {
      setErrorMessage((err as Error).message)
      toast({ tone: 'error', text: 'Failed to save: ' + (err as Error).message })
    }
  }

  const removeUsageRow = (idx: number) => setUsages(usages.filter((_, i) => i !== idx))
  const updateUsage = (idx: number, key: keyof UsageDraft, val: string) =>
    setUsages(usages.map((u, i) => (i === idx ? { ...u, [key]: val } : u)))

  const isPending = isEdit ? updatePrint.isPending : createPrint.isPending || createProduct.isPending

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: 600 }}>
      <BackLink fallback="/prints" />
      <h1>{isEdit ? 'Edit print' : 'Log a print'}</h1>
      <form onSubmit={handleSubmit} style={{ marginTop: 'var(--space-6)' }}>
        {/* Name — first field, prominent, required for business-sale */}
        <Field
          label="Name"
          value={description}
          onChange={setDescription}
          hint="What did you print? e.g., 'Articulated dragon - large'"
          required={purpose === 'business-sale'}
        />
        <Field
          label="Model source"
          value={modelSource}
          onChange={setModelSource}
          hint="Where did the file come from? e.g., 'Printables: dragon-by-xyz', 'MakerWorld: ...', 'self-designed'"
        />

        {!isEdit && (
          <Field
            label="Printer"
            value={effectivePrinterId}
            onChange={setPrinterId}
            selectOptions={(printers ?? []).map((p) => ({ value: p.id, label: p.name }))}
            required
          />
        )}
        <Field label="Date" value={printDate} onChange={setPrintDate} type="date" required />
        <Field
          label="Purpose"
          value={purpose}
          onChange={(v) => setPurpose(v as typeof PRINT_PURPOSES[number])}
          select={PRINT_PURPOSES as unknown as string[]}
        />
        {purpose === 'business-sale' && (
          <>
            <Field
              label="Product"
              value={productId}
              onChange={(v) => {
                setProductId(v)
                if (v !== '__new__') setNewProductName('')
              }}
              selectOptions={[
                { value: '', label: '— Custom one-off (no product) —' },
                ...(products ?? []).map((p) => ({ value: p.id, label: p.name })),
                { value: '__new__', label: '+ Create new product…' },
              ]}
              hint="Pick an existing product, or '+ Create new product' to add one inline"
            />
            {isCreatingNewProduct && (
              <Field
                label="New product name"
                value={newProductName}
                onChange={setNewProductName}
                required
                hint="This product will be created when you save the print"
              />
            )}
            {/* Build picker — visible in both create AND edit modes when a product is selected */}
            {(productId !== '' || isCreatingNewProduct) && (() => {
              const targetProductId = isCreatingNewProduct ? null : productId
              const productBuilds = (builds ?? []).filter(
                (b) => b.product_id === targetProductId && b.status === 'in_progress',
              )
              // In edit mode, the currently-selected build might be completed — include it
              // in the options so it shows up rather than appearing blank.
              // Even if the build belongs to a different product, surface it
              // here — on save we'll move the build to the chosen product.
              const currentBuild = builds?.find((b) => b.id === buildSelection)
              const showCurrent =
                !!currentBuild &&
                !productBuilds.some((b) => b.id === currentBuild.id)
              const currentBuildIsForeign =
                showCurrent && currentBuild.product_id !== targetProductId
              return (
                <>
                  <Field
                    label={isEdit ? 'Attached to build' : 'Add this print to a build'}
                    value={buildSelection}
                    onChange={setBuildSelection}
                    selectOptions={[
                      ...(showCurrent
                        ? [{
                            value: currentBuild.id,
                            label: `${currentBuild.name ?? 'Build ' + currentBuild.id.slice(0, 6)}${currentBuildIsForeign ? ' (will move to this product)' : ` (current, ${currentBuild.status})`}`,
                          }]
                        : []),
                      { value: '__new_single__', label: 'New build — single-piece (this print = 1 unit)' },
                      { value: '__new_multi__', label: 'New multi-piece build (in-progress, more prints to come)' },
                      ...productBuilds.map((b) => ({
                        value: b.id,
                        label: `${b.name ?? 'Build ' + b.id.slice(0, 6)} (in progress)`,
                      })),
                      { value: '__detached__', label: '— Not a build (test, scrap, etc.) —' },
                    ]}
                  />
                  {buildSelection === '__new_single__' && (
                    <div style={{
                      background: 'var(--color-primary-soft)',
                      color: 'var(--color-primary-text)',
                      padding: 'var(--space-2) var(--space-3)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--font-size-xs)',
                      marginTop: 'calc(var(--space-2) * -1)',
                      marginBottom: 'var(--space-3)',
                    }}>
                      ✓ This will add {Number(unitsProduced) || 1} unit{(Number(unitsProduced) || 1) === 1 ? '' : 's'} to inventory immediately.
                    </div>
                  )}
                  {buildSelection === '__new_multi__' && (
                    <div style={{
                      background: 'var(--color-warning, #f59e0b)',
                      color: 'white',
                      padding: 'var(--space-2) var(--space-3)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--font-size-xs)',
                      marginTop: 'calc(var(--space-2) * -1)',
                      marginBottom: 'var(--space-3)',
                      opacity: 0.9,
                    }}>
                      Build will start &ldquo;in progress.&rdquo; Inventory won&apos;t go up until you mark it complete on the product detail page.
                    </div>
                  )}
                </>
              )
            })()}
          </>
        )}
        {/* Units produced — hidden for business-sale unless it's a new build (fix 2) */}
        {purpose !== 'business-sale' && (
          <Field label="Units produced" value={unitsProduced} onChange={setUnitsProduced} type="decimal" />
        )}
        {purpose === 'business-sale' && buildSelection !== '__detached__' && (
          <Field
            label="Units produced"
            value={unitsProduced}
            onChange={setUnitsProduced}
            type="decimal"
            hint={
              buildSelection === '__new_single__'
                ? "How many finished units came off this plate? e.g., 15 keychains = 15"
                : buildSelection === '__new_multi__'
                ? "Eventually how many units will this build produce? (you can edit later)"
                : "Total units this build will produce (updates the build)"
            }
          />
        )}

        {/* Secondary outputs — additional products produced by the same plate */}
        {purpose === 'business-sale' && (
          <SecondaryOutputsSection
            outputs={secondaryOutputs}
            setOutputs={setSecondaryOutputs}
            products={products ?? []}
            builds={builds ?? []}
          />
        )}

        {/* Filament used — shown in both create and edit modes (fix 1) */}
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <label
            style={{
              display: 'block',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              marginBottom: 'var(--space-2)',
            }}
          >
            Filament used
          </label>

          {/* Color-bubble grid of available spools */}
          <div style={{ marginBottom: 'var(--space-3)' }}>
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', marginBottom: 'var(--space-2)' }}>
              Tap a spool to add it. For multi-color prints, tap multiple.
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                gap: 'var(--space-2)',
              }}
            >
              {(spools ?? [])
                .filter((s) => s.status === 'in-use')
                .map((s) => {
                  const alreadySelected = usages.some((u) => u.spoolId === s.id)
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => {
                        if (alreadySelected) return
                        setUsages([...usages, { spoolId: s.id, grams: '' }])
                      }}
                      disabled={alreadySelected}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 'var(--space-1)',
                        padding: 'var(--space-2)',
                        background: alreadySelected ? 'var(--color-primary-soft)' : 'var(--color-surface)',
                        border: alreadySelected
                          ? '2px solid var(--color-primary)'
                          : '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-md)',
                        cursor: alreadySelected ? 'default' : 'pointer',
                        opacity: alreadySelected ? 0.7 : 1,
                      }}
                      title={alreadySelected ? 'Already selected below' : 'Add to this print'}
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: s.color_hex || '#999',
                          border: '2px solid var(--color-border)',
                          flexShrink: 0,
                        }}
                      />
                      <div
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: 600,
                          textAlign: 'center',
                          lineHeight: 1.2,
                          color: 'var(--color-text)',
                          width: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {s.brand && (
                          <div style={{ color: 'var(--color-text-faint)', fontWeight: 500 }}>
                            {s.brand.trim()}
                          </div>
                        )}
                        <div>{s.color}</div>
                      </div>
                      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
                        {s.current_weight_grams}g
                      </div>
                      {alreadySelected && (
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-primary)', fontWeight: 700 }}>
                          ✓ added
                        </div>
                      )}
                    </button>
                  )
                })}
            </div>
          </div>

          {/* Selected filament rows with grams input */}
          {usages.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-faint)',
                  marginBottom: 'var(--space-2)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                }}
              >
                Selected — enter grams used:
              </div>
              {usages.map((u, idx) => {
                const spool = spoolById.get(u.spoolId)
                return (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      gap: 'var(--space-2)',
                      alignItems: 'center',
                      marginBottom: 'var(--space-2)',
                      padding: 'var(--space-2)',
                      background: 'var(--color-surface)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--color-border)',
                    }}
                  >
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: spool?.color_hex || '#999',
                        border: '1px solid var(--color-border)',
                        flexShrink: 0,
                      }}
                    />
                    <div
                      style={{
                        flex: 1,
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {spool ? `${spool.brand ? spool.brand.trim() + ' ' : ''}${spool.color}` : 'Unknown spool'}
                    </div>
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="grams"
                      value={u.grams}
                      onChange={(e) => updateUsage(idx, 'grams', e.target.value)}
                      style={{
                        width: 80,
                        padding: 'var(--space-2)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--color-border)',
                        background: 'var(--color-bg)',
                        color: 'var(--color-text)',
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => removeUsageRow(idx)}
                      style={{
                        color: 'var(--color-danger)',
                        padding: '0 var(--space-2)',
                        fontSize: 'var(--font-size-base)',
                        fontWeight: 700,
                      }}
                      title="Remove this spool"
                    >
                      ×
                    </button>
                  </div>
                )
              })}
            </div>
          )}

          {/* If no spools in inventory, show empty state */}
          {(spools ?? []).filter((s) => s.status === 'in-use').length === 0 && (
            <div
              style={{
                padding: 'var(--space-4)',
                background: 'var(--color-surface-2)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-text-muted)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              No filament spools yet. Add some on the Filament tab first.
            </div>
          )}
        </div>

        <Field
          label="Print time (hours)"
          value={printTimeHours}
          onChange={setPrintTimeHours}
          type="decimal"
          required
          hint="e.g., 4.5 for 4h 30m"
        />
        <Field
          label="Labor minutes"
          value={laborMinutes}
          onChange={setLaborMinutes}
          type="decimal"
          hint="Post-processing time (sanding, cleanup)"
        />
        <Field
          label="Outcome"
          value={outcome}
          onChange={(v) => setOutcome(v as typeof PRINT_OUTCOMES[number])}
          select={PRINT_OUTCOMES as unknown as string[]}
        />

        {!isEdit && costBreakdown && (
          <div
            style={{
              background: 'var(--color-surface)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-3) var(--space-4)',
              marginBottom: 'var(--space-4)',
            }}
          >
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-faint)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 600,
                marginBottom: 'var(--space-2)',
              }}
            >
              Cost breakdown
            </div>
            <CostRow label="Filament" value={costBreakdown.filamentCost} />
            <CostRow label="Machine (capital + electricity + buffer)" value={costBreakdown.machineCost} />
            <CostRow label="Labor" value={costBreakdown.laborCost} />
            <div
              style={{
                borderTop: '1px solid var(--color-border)',
                marginTop: 'var(--space-2)',
                paddingTop: 'var(--space-2)',
              }}
            >
              <CostRow label="Total" value={costBreakdown.totalCost} bold />
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => setShowDetails(!showDetails)}
          style={{
            width: '100%',
            padding: 'var(--space-3)',
            border: '1px dashed var(--color-border)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-primary)',
            fontWeight: 600,
            fontSize: 'var(--font-size-sm)',
            marginBottom: 'var(--space-4)',
          }}
        >
          {showDetails ? '− Hide detailed settings' : '+ Add detailed settings & notes'}
        </button>

        {showDetails && (
          <div
            style={{
              background: 'var(--color-surface-2)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-3)',
              marginBottom: 'var(--space-4)',
            }}
          >
            <h3 style={{ fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-3)' }}>
              Slicer settings
            </h3>
            <Field
              label="Slicer"
              value={slicerName}
              onChange={setSlicerName}
              select={['', 'Bambu Studio', 'OrcaSlicer', 'PrusaSlicer', 'Cura', 'Other']}
            />
            <Field label="Profile" value={slicerProfile} onChange={setSlicerProfile} />
            <Field label="Layer height (mm)" value={layerHeight} onChange={setLayerHeight} type="decimal" />
            <Field label="Infill %" value={infillPct} onChange={setInfillPct} type="decimal" />
            <Field
              label="Infill pattern"
              value={infillPattern}
              onChange={setInfillPattern}
              hint="gyroid, grid, cubic, etc."
            />
            <Field label="Speed (mm/s)" value={printSpeed} onChange={setPrintSpeed} type="decimal" />
            <Field label="Nozzle temp (°C)" value={nozzleTemp} onChange={setNozzleTemp} type="decimal" />
            <Field label="Bed temp (°C)" value={bedTemp} onChange={setBedTemp} type="decimal" />
            <Field
              label="Supports"
              value={supports}
              onChange={setSupports}
              select={['', 'none', 'tree', 'tree-organic', 'normal']}
            />

            <h3
              style={{
                fontSize: 'var(--font-size-sm)',
                margin: 'var(--space-4) 0 var(--space-3)',
              }}
            >
              Quality
            </h3>
            <Field
              label="Surface quality (1-5)"
              value={surfaceQuality}
              onChange={setSurfaceQuality}
              type="decimal"
            />
            <Field
              label="Dimensional accuracy (1-5)"
              value={dimensionalAccuracy}
              onChange={setDimensionalAccuracy}
              type="decimal"
            />
            <Field
              label="Failure mode (if any)"
              value={failureMode}
              onChange={setFailureMode}
              select={['', 'clog', 'layer-shift', 'warping', 'stringing', 'adhesion', 'first-layer', 'other']}
            />
            <Field label="Observations" value={observations} onChange={setObservations} multiline />
            <Field label="Lessons learned" value={lessonsLearned} onChange={setLessonsLearned} multiline />
          </div>
        )}

        <button
          type="submit"
          disabled={isPending}
          style={{
            padding: 'var(--space-3) var(--space-5)',
            background: 'var(--color-primary)',
            color: 'white',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
          }}
        >
          {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Save print'}
        </button>

        {errorMessage && (
          <p
            style={{
              marginTop: 'var(--space-3)',
              color: 'var(--color-danger)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {errorMessage}
          </p>
        )}
      </form>
    </div>
  )
}

function CostRow({ label, value, bold }: { label: string; value: number; bold?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: 'var(--space-1) 0',
        fontSize: 'var(--font-size-sm)',
        fontWeight: bold ? 700 : 400,
      }}
    >
      <span>{label}</span>
      <span>{formatMoney(value)}</span>
    </div>
  )
}

function SecondaryOutputsSection({
  outputs,
  setOutputs,
  products,
  builds,
}: {
  outputs: SecondaryOutputDraft[]
  setOutputs: (next: SecondaryOutputDraft[]) => void
  products: { id: string; name: string }[]
  builds: Build[]
}) {
  const addRow = () =>
    setOutputs([
      ...outputs,
      { productId: '', newProductName: '', buildSelection: '__new_single__', units: '1' },
    ])
  const removeRow = (idx: number) => setOutputs(outputs.filter((_, i) => i !== idx))
  const patchRow = (idx: number, patch: Partial<SecondaryOutputDraft>) =>
    setOutputs(outputs.map((o, i) => (i === idx ? { ...o, ...patch } : o)))

  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      {outputs.map((o, idx) => {
        const isNew = o.productId === '__new__'
        const targetProductId = isNew ? null : o.productId
        const productBuilds = builds.filter(
          (b) => b.product_id === targetProductId && b.status === 'in_progress',
        )
        const currentBuild = builds.find((b) => b.id === o.buildSelection)
        const showCurrent =
          currentBuild &&
          !productBuilds.some((b) => b.id === currentBuild.id) &&
          currentBuild.product_id === targetProductId
        return (
          <div
            key={idx}
            style={{
              background: 'var(--color-surface-2)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-3)',
              marginBottom: 'var(--space-3)',
              border: '1px dashed var(--color-border)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 'var(--space-2)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-faint)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 600,
              }}
            >
              <span>Additional product on this plate</span>
              <button
                type="button"
                onClick={() => removeRow(idx)}
                style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-xs)' }}
              >
                × Remove
              </button>
            </div>

            <Field
              label="Product"
              value={o.productId}
              onChange={(v) => patchRow(idx, { productId: v, ...(v !== '__new__' ? { newProductName: '' } : {}) })}
              selectOptions={[
                { value: '', label: '— Pick a product —' },
                ...products.map((p) => ({ value: p.id, label: p.name })),
                { value: '__new__', label: '+ Create new product…' },
              ]}
            />
            {isNew && (
              <Field
                label="New product name"
                value={o.newProductName}
                onChange={(v) => patchRow(idx, { newProductName: v })}
                required
              />
            )}
            {(o.productId !== '' || isNew) && (
              <Field
                label="Build"
                value={o.buildSelection}
                onChange={(v) => patchRow(idx, { buildSelection: v })}
                selectOptions={[
                  ...(showCurrent && currentBuild
                    ? [{
                        value: currentBuild.id,
                        label: `${currentBuild.name ?? 'Build ' + currentBuild.id.slice(0, 6)} (current, ${currentBuild.status})`,
                      }]
                    : []),
                  { value: '__new_single__', label: 'New single-piece build' },
                  { value: '__new_multi__', label: 'New multi-piece build (in progress)' },
                  ...productBuilds.map((b) => ({
                    value: b.id,
                    label: `${b.name ?? 'Build ' + b.id.slice(0, 6)} (in progress)`,
                  })),
                  { value: '__detached__', label: '— Not a build —' },
                ]}
              />
            )}
            {o.buildSelection && o.buildSelection !== '__detached__' && o.buildSelection !== '' && (
              <Field
                label="Units"
                value={o.units}
                onChange={(v) => patchRow(idx, { units: v })}
                type="decimal"
              />
            )}
          </div>
        )
      })}
      <button
        type="button"
        onClick={addRow}
        style={{
          width: '100%',
          padding: 'var(--space-2) var(--space-3)',
          border: '1px dashed var(--color-border)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--color-primary)',
          fontWeight: 600,
          fontSize: 'var(--font-size-sm)',
          background: 'transparent',
        }}
      >
        + Add another product to this plate
      </button>
    </div>
  )
}
